import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Database, LayoutDashboard, Boxes, AlertTriangle, FileText, BarChart3, Truck, Calculator } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LabelPrinting from "@/components/LabelPrinting";
import ThemeToggle from "@/components/ThemeToggle";
import { useSubscription } from "@/context/SubscriptionContext";
import { useModuleAuth } from "@/hooks/useModuleAuth";
import { AuthCheckSkeleton } from "@/components/AuthCheckSkeleton";

const StoreLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  // Pro-only per-module auth wall (no-op on Core).
  const checking = useModuleAuth({ role: "store", loginPath: "/store-login" });

  const allNavItems = [
    { path: "/store/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/store/inventory", label: "Inventory", icon: Boxes },
    { path: "/store/database", label: "Database", icon: Database },
    { path: "/store/low-stock", label: "Low Stock", icon: AlertTriangle },
    { path: "/store/purchase-order", label: "PO Form", icon: FileText },
    { path: "/store/delivery-challan", label: "DC", icon: Truck, feature: "delivery_challans" },
    { path: "/store/predictor", label: "Predictor", icon: Calculator, feature: "cost_calculator" },
    { path: "/store/reports", label: "Reports", icon: BarChart3 },
  ];
  const navItems = allNavItems.filter((i) => !i.feature || hasFeature(i.feature));

  // Auth removed: Store module is openly accessible.

  const handleLogout = async () => {
    try { const { logout } = await import("@/utils/auth"); await logout(); } catch { /* ignore */ }
    navigate("/");
  };

  if (checking) return <AuthCheckSkeleton />;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      {/* Navigation */}
      <nav className="bg-black/90 shadow-2xl border-b-2 border-emerald-500/40 backdrop-blur-sm relative">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-slate-500/5 to-transparent"></div>
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex justify-between items-center h-14 gap-4">
            <div className="flex items-center gap-6 min-w-0">
              {/* Logo - clickable to dashboard */}
              <div className="cursor-pointer shrink-0" onClick={() => navigate("/store/dashboard")}>
                <Logo size="md" showText={false} />
              </div>

              {/* Nav Items */}
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                      className={`inline-flex items-center whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide transition-all ${
                        isActive
                          ? "bg-cyan-500/15 text-cyan-400 ring-1 ring-cyan-400/50"
                          : "text-gray-400 hover:text-cyan-300 hover:bg-white/5"
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      {item.label}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <LabelPrinting />
              <ThemeToggle />
              <Button
                data-testid="store-logout-button"
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="h-8 border-slate-500/50 text-cyan-400 hover:bg-cyan-500 hover:text-black font-semibold text-xs"
              >
                <LogOut className="h-3.5 w-3.5 mr-1" />
                Logout
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm" className="h-8 text-cyan-400 hover:bg-white/5 text-xs">
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                  Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default StoreLayout;
